import React, { useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Button,
  SimpleGrid,
  Text,
  Badge,
  Divider,
  Icon,
  Select,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Wrap,
  WrapItem,
  Code,
} from '@chakra-ui/react';
import { QRCodeCanvas } from 'qrcode.react';
import { FiArrowLeft, FiSlash, FiMousePointer, FiDownload, FiPercent, FiExternalLink, FiSmartphone } from 'react-icons/fi';
import { useGetLinkQuery, useGetAnalyticsQuery } from '../store/linksApi';
import { LoadingState, ErrorState } from '../components/common/States';
import { StatCard } from '../components/common/StatCard';
import { LinkStatusBadge } from '../components/links/LinkStatusBadge';
import { ShortUrlCopy } from '../components/links/ShortUrlCopy';
import { ClicksOverTimeChart } from '../components/analytics/ClicksOverTimeChart';
import { BreakdownChart } from '../components/analytics/BreakdownChart';
import { ColumnChart } from '../components/analytics/ColumnChart';
import { ClicksMapChart } from '../components/analytics/ClicksMapChart';
import { ActivityLog } from '../components/analytics/ActivityLog';
import { ExpireLinkDialog } from '../components/links/ExpireLinkDialog';
import { formatDate, formatDateTime, formatDateTimeTz, formatPercent } from '../lib/format';
import { PURPOSE_LABELS, type Link } from '../types/link';
import { showInstallMetrics, PURPOSE_CONFIG_FIELDS } from '../lib/linkMetrics';

/** Human labels for every configuration field we can render. */
const FIELD_LABELS: Partial<Record<keyof Link, string>> = {
  linkName: 'Link name',
  campaignName: 'Campaign name',
  campaignId: 'Campaign ID',
  destination: 'Destination',
  websiteUrl: 'Website URL',
  iosAppStoreUrl: 'iOS App Store',
  androidAppStoreUrl: 'Android Play Store',
  ipadAppStoreUrl: 'iPad-only App Store',
  huaweiAppGalleryUrl: 'Huawei AppGallery',
  webFallbackUrl: 'Web fallback',
  iosDeepLink: 'iOS deep link',
  androidDeepLink: 'Android deep link',
  appScheme: 'App scheme',
  iosUniversalLink: 'iOS Universal Link',
  androidAppLink: 'Android App Link',
  deepLinkPath: 'Deep-link path',
};

/** Fields shown for every purpose (SM_002). Contact ref removed (SM_005). */
const COMMON_FIELDS: Array<keyof Link> = ['linkName', 'campaignName', 'campaignId', 'destination'];

/**
 * SM_002: Build the "Link configuration" rows from the link's purpose so a
 * Website link no longer shows empty App Store / deep-link rows. Common fields
 * always show; purpose-specific fields show only when they carry a value.
 */
function buildConfigRows(link: Link): Array<{ label: string; value?: string }> {
  const rows: Array<{ label: string; value?: string }> = COMMON_FIELDS.map((key) => ({
    label: FIELD_LABELS[key] ?? key,
    value: link[key] as string | undefined,
  }));
  for (const key of PURPOSE_CONFIG_FIELDS[link.purpose] ?? []) {
    const value = link[key] as string | undefined;
    if (value) rows.push({ label: FIELD_LABELS[key] ?? key, value });
  }
  return rows;
}

// Permission gate (added by host app — see SmartLinks.jsx): defaults to true
// so this behaves exactly as before when rendered without the prop.
interface LinkDetailPageProps {
  editPermission?: boolean;
}

export const LinkDetailPage: React.FC<LinkDetailPageProps> = ({ editPermission = true }) => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [windowDays, setWindowDays] = useState(30);
  const [expiring, setExpiring] = useState<Link | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  const linkQ = useGetLinkQuery(id);
  const analyticsQ = useGetAnalyticsQuery({ id, windowDays });

  const downloadQr = useCallback(() => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${linkQ.data?.shortCode ?? 'qr'}-qrcode.png`;
    a.click();
  }, [linkQ.data?.shortCode]);

  if (linkQ.isLoading) return <LoadingState label="Loading link…" />;
  if (linkQ.isError || !linkQ.data) {
    return (
      <ErrorState
        message={(linkQ.error as { message?: string })?.message ?? 'Link not found'}
        onRetry={linkQ.refetch}
      />
    );
  }

  const link = linkQ.data;
  const a = analyticsQ.data;
  const showInstalls = showInstallMetrics(link); // SM_003 — gate install metrics by purpose
  const configRows = buildConfigRows(link); // SM_002 — purpose-specific config fields

  return (
    <Box>
      <Breadcrumb fontSize="sm" color="gray.500" mb={3}>
        <BreadcrumbItem>
          {/* SL_003: one route up reaches the list; '../..' overshot to login. */}
          <BreadcrumbLink onClick={() => navigate('..')}>Smart Links</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>{link.shortCode}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Flex align="center" gap={4} mb={6} wrap="wrap">
        <Button leftIcon={<FiArrowLeft />} variant="ghost" size="sm" onClick={() => navigate('..')}>
          Back
        </Button>
        <VStack align="start" spacing={1}>
          <Heading size="md">{link.linkName}</Heading>
          <HStack>
            <ShortUrlCopy url={link.shortUrl} />
            <LinkStatusBadge status={link.status} />
            <Badge bg="var(--brand-50)" color="var(--brand-800)">
              {PURPOSE_LABELS[link.purpose]}
            </Badge>
          </HStack>
          <Text fontSize="xs" color="gray.400">
            Created {formatDate(link.createdAt)} · Expires {link.expiresAt ? formatDateTimeTz(link.expiresAt) : 'never'} ·
            Attribution window {link.attributionWindowHours}h
          </Text>
        </VStack>
        <Box flex={1} />
        <HStack>
          <Select
            size="sm"
            maxW="140px"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            bg="white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </Select>
          <Button
            leftIcon={<FiSlash />}
            size="sm"
            colorScheme="red"
            variant="outline"
            isDisabled={link.status !== 'active' || !editPermission}
            onClick={() => setExpiring(link)}
          >
            Disable
          </Button>
        </HStack>
      </Flex>

      <Flex gap={4} mb={6} direction={{ base: 'column', lg: 'row' }} align="stretch">
        {/* Stat cards on the left in a 2×2 grid — Clicks / Unique devices on the
            top row, Installs / Install rate below them; QR code panel sits on
            the right. */}
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} flex={1}>
          <StatCard
            label="Clicks"
            value={Number(analyticsQ.data?.totals.clicks ?? link.metrics?.clicks ?? 0).toLocaleString()}
            icon={FiMousePointer}
          />
          <StatCard
            label="Unique devices"
            value={Number(analyticsQ.data?.totals.uniqueDevices ?? 0).toLocaleString()}
            icon={FiSmartphone}
            accent="purple.500"
          />
          {/* SM_003 — Installs + Install rate only for links that can attribute an install */}
          {showInstalls && (
            <>
              <StatCard
                label="Installs"
                value={Number(analyticsQ.data?.totals.installs ?? link.metrics?.installs ?? 0).toLocaleString()}
                icon={FiDownload}
                accent="teal.500"
              />
              <StatCard
                label="Install rate"
                value={formatPercent(analyticsQ.data?.totals.installRate ?? link.metrics?.installRate ?? 0)}
                icon={FiPercent}
                accent="orange.500"
              />
            </>
          )}
        </SimpleGrid>
        <Box
          bg="white"
          borderWidth="1px"
          rounded="lg"
          p={4}
          shadow="xs"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={3}
          minW={{ lg: '220px' }}
        >
          <Text fontSize="xs" fontWeight={600} color="gray.500" letterSpacing="0.04em" textTransform="uppercase" alignSelf="flex-start">
            QR code
          </Text>
          <Box ref={qrRef} bg="white" p={2}>
            <QRCodeCanvas value={link.shortUrl} size={120} level="M" includeMargin={false} />
          </Box>
          <Button leftIcon={<FiDownload />} size="xs" variant="outline" onClick={downloadQr} w="full">
            Download
          </Button>
        </Box>
      </Flex>

      {analyticsQ.isError ? (
        <ErrorState
          message={(analyticsQ.error as { message?: string })?.message ?? "Couldn't load analytics."}
          onRetry={analyticsQ.refetch}
        />
      ) : analyticsQ.isLoading || !a ? (
        <LoadingState label="Loading analytics…" />
      ) : (
        <VStack align="stretch" spacing={6}>
          <ClicksOverTimeChart data={a.timeseries} showInstalls={showInstalls} />
          {/* Geographic overview — clicks from all devices, by country (drill into regions) */}
          <ClicksMapChart data={a.byCountry ?? []} regionData={a.byRegion ?? []} />
          {/* Platform donut paired with the first ranked dimension */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            <BreakdownChart title="Clicks by platform" data={a.byPlatform} />
            <ColumnChart title="Clicks by country" data={a.byCountry ?? []} />
          </SimpleGrid>
          {/* Column charts (ranked dimensions) */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            <ColumnChart title="Clicks by city" data={a.byCity ?? []} />
            <ColumnChart title="Clicks by device model" data={a.byDeviceModel ?? []} />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            <ColumnChart title="Clicks by OS" data={a.byOS ?? []} />
            <ColumnChart title="Clicks by OS version" data={a.byOSVersion ?? []} />
          </SimpleGrid>
          <ActivityLog events={a.recentEvents} title="Activity log — every click, install & open" />
        </VStack>
      )}

      <Box bg="white" borderWidth="1px" rounded="lg" p={5} shadow="xs" mt={6}>
        <Heading size="xs" color="gray.600" mb={4}>
          Link configuration
        </Heading>
        <VStack align="stretch" spacing={0} divider={<Divider />}>
          {configRows.map((row) => (
            <HStack key={row.label} justify="space-between" py={2} align="start">
              <Text fontSize="sm" color="gray.500" minW="160px">
                {row.label}
              </Text>
              <Text fontSize="sm" textAlign="right" wordBreak="break-all">
                {row.value ?? <Text as="span" color="gray.300">—</Text>}
              </Text>
            </HStack>
          ))}
          {(link.utmParameters || link.deepLinkParameters) && (
            <Box py={2}>
              <Text fontSize="sm" color="gray.500" mb={2}>
                Parameters
              </Text>
              <Wrap>
                {Object.entries({ ...link.utmParameters, ...link.deepLinkParameters }).map(([k, v]) => (
                  <WrapItem key={k}>
                    <Code fontSize="xs">
                      {k}={v}
                    </Code>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}
        </VStack>
      </Box>

      <Flex mt={4} color="gray.400" fontSize="xs" gap={2}>
        <Icon as={FiExternalLink} />
        <Text>Last updated {formatDateTime(link.updatedAt)}</Text>
      </Flex>

      <ExpireLinkDialog
        link={expiring}
        onClose={() => {
          setExpiring(null);
          linkQ.refetch();
        }}
      />
    </Box>
  );
};
